import React from 'react';
import { Status, Size } from '@uds/button';
import { CTA } from 'core/ribbons/cta';
import { Product } from '@uds/product';
import { Glyph, IconName } from '@uds/glyphs';
import callToAction from 'presentation/hocs/callToAction';
import { omit } from 'presentation/utils/generalUtils';
import { ReactRouterButton } from '@uds/react-router-adapter';
import { useIsExternalRoute } from 'domain/selectors/common';

interface OuterProps extends CTA {
  product?: Product;
  block?: boolean;
  size?: Size;
  ribbonId: string;
  index: number;
  glyph?: IconName;
}

interface InnerProps extends OuterProps {
  addingStatus?: Status;
  handleClick: (props: any) => () => void;
}

const getStatus = (addingStatus: string | undefined) => {
  switch (addingStatus) {
    case 'PENDING':
      return Status.PENDING;
    case 'SUCCESS':
      return Status.SUCCESS;
    case 'FAILURE':
      return Status.FAILURE;
    default:
      return Status.DEFAULT;
  }
};

const PureCallToAction = ({ kind, label, addingStatus, handleClick, behavior, ...props }: InnerProps) => {
  const status = getStatus(addingStatus);
  const goto = 'goto' in behavior ? behavior.goto : undefined;
  const [isExternal, url] = useIsExternalRoute(goto);
  const newTab = 'newTab' in behavior ? behavior.newTab : null;
  const rel = 'rel' in behavior ? behavior.rel : undefined;

  const openNewWindowProps =
    newTab === 'true'
      ? {
          rel: rel,
          target: '_blank',
        }
      : {};

  const buttonProps = omit(
    [
      'actions',
      'buttonId',
      'ribbonId',
      'index',
      'handlePlayVideoClick',
      'handleSetVisibilityClick',
      'handleConfigureClick',
      'handleAddToBasketClick',
      'handleNavigateClick',
      'handleAnalytics',
      'ribbonList',
      'glyph',
    ],
    props,
  );

  return (
    <ReactRouterButton
      kind={kind}
      status={status}
      onClick={handleClick}
      href={url}
      isExternalRoute={isExternal}
      {...openNewWindowProps}
      {...buttonProps}
      glyph={props.glyph && <Glyph name={props.glyph} alt={props.glyph} />}
    >
      {label}
    </ReactRouterButton>
  );
};

PureCallToAction.displayName = 'CallToAction';

export default callToAction(PureCallToAction);
